import { Navigate, Route, Routes, Link, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'
import Login from './components/Login'
import StreamList from './components/StreamList'
import CreateStream from './components/CreateStream'
import StudioRoom from './components/StudioRoom'
import ProductDetail from './components/ProductDetail'
import ShopLayout from './components/shop/ShopLayout'
import Home from './components/shop/Home'
import Cart from './components/shop/Cart'
import Checkout from './components/shop/Checkout'
import SellerNav from './components/seller/SellerNav'
import MyStore from './components/seller/MyStore'
import StoreSettings from './components/seller/StoreSettings'
import StoresList from './components/seller/StoresList'
import StoreEditor from './components/seller/StoreEditor'
import ProductsList from './components/seller/ProductsList'
import ProductEditor from './components/seller/ProductEditor'
import ProductVariants from './components/seller/ProductVariants'
import ProductAssignment from './components/seller/ProductAssignment'
import MallSettings from './components/seller/MallSettings'
import { RequireRole } from './components/seller/RequireRole'
import { canManageMall } from './auth/roles'

function RequireAuth({ children }: { children: JSX.Element }) {
  const { token } = useAuth()
  const location = useLocation()
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  return children
}

// Seller studio chrome. Distinct from the shopper ShopLayout — only the
// seller side shows the "Tempest Studio" nav + role badge.
function SellerLayout() {
  const { user, clear } = useAuth()
  return (
    <>
      {user && (
        <header className="navbar">
          <div className="navbar-inner">
            <Link to="/" className="navbar-brand">Tempest Studio</Link>
            <div className="navbar-spacer" />
            <span className="navbar-user">
              {user.fullName} · <span className="navbar-role">{user.role}</span>
            </span>
            <button className="btn-link" onClick={clear}>Sign out</button>
          </div>
          <SellerNav />
        </header>
      )}
      <main className="page"><Outlet /></main>
    </>
  )
}

export default function App() {
  return (
    <Routes>
      {/* Shopper storefront — all public; guests can browse, basket, and
          talk to the avatar. Wrapped in ShopLayout (header + cart badge). */}
      <Route element={<ShopLayout />}>
        <Route path="/shop" element={<Home />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
      </Route>

      {/* Shared auth screen. */}
      <Route path="/login" element={<Login />} />

      {/* Seller studio (livestreaming + management) — login-gated. */}
      <Route element={<SellerLayout />}>
        <Route path="/" element={<RequireAuth><StreamList /></RequireAuth>} />
        <Route path="/streams/new" element={<RequireAuth><CreateStream /></RequireAuth>} />
        <Route path="/streams/:id/go-live" element={<RequireAuth><StudioRoom /></RequireAuth>} />
        {/* Seller tier — own store (Phase 1). */}
        <Route path="/manage/store" element={<RequireAuth><MyStore /></RequireAuth>} />
        <Route path="/manage/store/settings" element={<RequireAuth><StoreSettings /></RequireAuth>} />
        {/* Mall-owner — store management (Phase 2). */}
        <Route path="/manage/stores" element={<RequireRole allow={canManageMall}><StoresList /></RequireRole>} />
        <Route path="/manage/stores/new" element={<RequireRole allow={canManageMall}><StoreEditor /></RequireRole>} />
        <Route path="/manage/stores/:id/edit" element={<RequireRole allow={canManageMall}><StoreEditor /></RequireRole>} />
        {/* Mall-owner — product management (Phase 3). */}
        <Route path="/manage/products" element={<RequireRole allow={canManageMall}><ProductsList /></RequireRole>} />
        <Route path="/manage/products/new" element={<RequireRole allow={canManageMall}><ProductEditor /></RequireRole>} />
        <Route path="/manage/products/:id/edit" element={<RequireRole allow={canManageMall}><ProductEditor /></RequireRole>} />
        <Route path="/manage/products/:id/variants" element={<RequireRole allow={canManageMall}><ProductVariants /></RequireRole>} />
        <Route path="/manage/products/:id/assign" element={<RequireRole allow={canManageMall}><ProductAssignment /></RequireRole>} />
        {/* Mall-owner — mall settings (Phase 4). */}
        <Route path="/manage/mall" element={<RequireRole allow={canManageMall}><MallSettings /></RequireRole>} />
      </Route>

      {/* Default landing is the storefront. */}
      <Route path="*" element={<Navigate to="/shop" replace />} />
    </Routes>
  )
}
